/**
 * HeartArrival — Heart-specific arrival moment
 * 
 * Gentle guidance to bring awareness to the heart space.
 * "Bring awareness gently to the centre of the chest... the heart space..."
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ARRIVAL_TEXTS = [
  { text: 'Take a moment\u2026', delay: 0 },
  { text: 'Let everything settle\u2026', delay: 3500 },
  { text: 'Bring your awareness\ngently to the centre\nof the chest\u2026', delay: 7500 },
  { text: 'The heart space\u2026', delay: 13000 },
];

const ARRIVAL_DURATION = 17000;

const HeartArrival = ({ onComplete, onSkip }) => {
  const [phase, setPhase] = useState('arrival');
  const [textIndex, setTextIndex] = useState(-1);
  const timersRef = useRef([]);

  useEffect(() => {
    ARRIVAL_TEXTS.forEach((item, i) => {
      const timer = setTimeout(() => setTextIndex(i), item.delay);
      timersRef.current.push(timer);
    });
    const endTimer = setTimeout(() => setPhase('choice'), ARRIVAL_DURATION);
    timersRef.current.push(endTimer);
    return () => timersRef.current.forEach(clearTimeout);
  }, []);

  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center"
      style={{ background: 'radial-gradient(ellipse at 50% 45%, rgba(30,22,14,1) 0%, rgba(10,8,5,1) 65%)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.5 }}
      data-testid="heart-arrival"
    >
      {/* Soft warm heart glow in center — breathing pulse */}
      <motion.div
        className="absolute pointer-events-none"
        style={{ top: '40%', left: '50%', transform: 'translate(-50%, -50%)' }}
        animate={{
          width: [55, 80, 55],
          height: [55, 80, 55],
          opacity: [0.12, 0.3, 0.12],
        }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
      >
        <div className="w-full h-full rounded-full" style={{
          background: 'radial-gradient(circle, rgba(225,185,135,0.35) 0%, rgba(215,175,125,0.1) 45%, transparent 65%)',
          filter: 'blur(12px)',
        }} />
      </motion.div>
      {/* Outer breath field */}
      <motion.div
        className="absolute pointer-events-none"
        style={{ top: '40%', left: '50%', transform: 'translate(-50%, -50%)' }}
        animate={{
          width: [100, 140, 100],
          height: [100, 140, 100],
          opacity: [0.04, 0.1, 0.04],
        }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
      >
        <div className="w-full h-full rounded-full" style={{
          background: 'radial-gradient(circle, rgba(210,175,130,0.2) 0%, transparent 55%)',
          filter: 'blur(18px)',
        }} />
      </motion.div>

      <AnimatePresence mode="wait">
        {phase === 'arrival' && textIndex >= 0 && (
          <motion.p
            key={textIndex}
            className="text-white/50 text-center text-lg px-10 leading-relaxed whitespace-pre-line"
            style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 1.5 }}
            data-testid="arrival-text"
          >
            {ARRIVAL_TEXTS[textIndex].text}
          </motion.p>
        )}

        {phase === 'choice' && (
          <motion.div
            key="choice"
            className="flex flex-col items-center gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          >
            <p
              className="text-white/40 text-base text-center px-8 leading-relaxed whitespace-pre-line"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
            >
              {'You are here\u2026\nand your heart is ready'}
            </p>

            <motion.button
              onClick={onComplete}
              className="px-8 py-3 rounded-full text-white/50 text-sm"
              style={{
                background: 'rgba(200,165,115,0.1)',
                border: '1px solid rgba(200,165,115,0.15)',
                fontFamily: "'Manrope', sans-serif",
              }}
              whileTap={{ scale: 0.96 }}
              data-testid="heart-arrival-continue-btn"
            >
              Continue
            </motion.button>

            <motion.button
              onClick={onSkip}
              className="text-white/20 text-xs"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              whileTap={{ scale: 0.96 }}
              data-testid="heart-arrival-skip-btn"
            >
              Skip for now
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default HeartArrival;
